import { createTheme, ThemeProvider } from '@mui/material/styles';
import { esES as coreEsEs } from '@mui/material/locale';
import { esES } from '@mui/x-data-grid';
import { useContext } from 'react';
import { ColorModeContext } from '../layouts/context/ColorMode';

// Paleta de colores base
const COLORS = {
  verde: '#4fae3c',
  azul: '#0f70b7',
  gradiente: 'linear-gradient(90deg, #4dae3c, #87be4d)',
  neutral: '#e0e0e0',
  neutralText: 'rgba(2,37,34,0.96)',
  primaryDark: '#647f45',
  secondaryDark: '#13588c',
};

// Tipografía base
const FONT_FAMILY = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

// Tema principal de la app
export const ConfigTheme = ({ children }) => {
  const { mode } = useContext(ColorModeContext);

  const getDesignTokens = (mode) => ({
    cssVariables: true,
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            // 🎨 PALETA LIGHT
            neutral: {
              main: COLORS.neutral,
              contrastText: COLORS.neutralText,
            },
            primary: {
              main: COLORS.verde,
              contrastText: 'rgba(255,255,255,0.93)',
              dark: COLORS.primaryDark,
            },
            secondary: {
              main: COLORS.azul,
              dark: COLORS.secondaryDark,
            },
            text: {
              primary: '#1A2027',
              secondary: 'rgba(0, 0, 0, 0.6)',
            },
            background: {
              default: '#f5f5f5',
              paper: '#ffffff',
            },
          }
        : {
            // 🌙 PALETA DARK
            neutral: {
              main: '#424242',
              contrastText: '#e0e0e0',
            },
            primary: {
              main: COLORS.verde,
              contrastText: '#ffffff',
              dark: '#3d8c2f',
            },
            secondary: {
              main: COLORS.azul,
              dark: '#0a4e82',
            },
            text: {
              primary: '#e0e0e0',
              secondary: '#bdbdbd',
            },
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
          }),
      gradient: {
        main: COLORS.gradiente,
      },
    },
    typography: {
      fontFamily: FONT_FAMILY,
      subtitle1: {
        color: mode === 'light' ? '#5f6368' : '#b0b0b0',
      },
      button: {
        fontWeight: 400,
      },
      body1: {
        color: mode === 'light' ? '#1A2027' : '#e0e0e0',
      },
      body2: {
        color: mode === 'light' ? 'rgba(0,0,0,0.6)' : '#bdbdbd',
      },
    },
    components: {
      MuiCard: {
        defaultProps: {
          variant: 'elevation',
          elevation: 2,
        },
        styleOverrides: {
          root: {
            padding: 8,
            borderRadius: '10px',
            boxShadow:
              mode === 'light'
                ? '0px 1px 5px 0px rgb(0 0 0 / 10%), 0px 2px 2px 0px rgb(0 0 0 / 10%), 0px 3px 1px -2px rgb(0 0 0 / 10%)'
                : '0px 1px 5px 0px rgb(0 0 0 / 40%), 0px 2px 2px 0px rgb(0 0 0 / 30%), 0px 3px 1px -2px rgb(0 0 0 / 20%)',
            backgroundColor: mode === 'light' ? '#fff' : '#1e1e1e',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor:
                mode === 'light' ? 'rgba(79, 174, 60, 0.1)' : 'rgba(79, 174, 60, 0.25)',
              color: COLORS.azul,
              fontWeight: 600,
            },
            '&.Mui-selected:hover': {
              backgroundColor:
                mode === 'light' ? 'rgba(79, 174, 60, 0.2)' : 'rgba(79, 174, 60, 0.35)',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'light' ? '#f5f5f5' : '#121212',
            transition: 'background-color 0.3s ease',
          },
        },
      },
    },
    esES,
    coreEsEs,
  });

  const theme = createTheme(getDesignTokens(mode));

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
